import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Plus, Trash2, Megaphone, Sparkles, AlertTriangle, X, ToggleLeft, ToggleRight, Send } from 'lucide-react';
import axios from 'axios';

const TYPE_OPTIONS = [
  { value: 'update', label: "What's New (System Update)", icon: Sparkles, color: 'blue' },
  { value: 'admin_message', label: 'Message from Dr. Ganz', icon: Megaphone, color: 'emerald' },
  { value: 'notice', label: 'Notice / Known Issue', icon: AlertTriangle, color: 'amber' },
];

export default function AdminAnnouncementsPanel({ adminToken }) {
  const getToken = () => adminToken || localStorage.getItem('adminToken');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '',
    type: 'admin_message',
    senderName: 'Dr. Ganz',
    version: '',
    items: [{ text: '', icon: null }],
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/list`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.data?.success) setAnnouncements(res.data.data);
    } catch (err) {
      console.error('Load announcements error:', err);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.title.trim() || form.items.every(i => !i.text.trim())) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/create`,
        { ...form, items: form.items.filter(i => i.text.trim()) },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setCreating(false);
      setForm({ title: '', type: 'admin_message', senderName: 'Dr. Ganz', version: '', items: [{ text: '', icon: null }] });
      loadAnnouncements();
    } catch (err) {
      console.error('Create error:', err);
    }
  };

  const toggleActive = async (id, currentActive) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/${id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      loadAnnouncements();
    } catch {}
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/${id}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      loadAnnouncements();
    } catch {}
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-blue-600" />
              Manage Announcements
            </CardTitle>
            {!creating && (
              <Button onClick={() => setCreating(true)} size="sm">
                <Plus className="h-4 w-4 mr-1" />
                New Announcement
              </Button>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Create announcements that appear when users log in. Users see them every login until they click "Don't show again".
          </p>
        </CardHeader>
        <CardContent>
          {/* Create Form */}
          {creating && (
            <div className="mb-6 p-5 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-4">Create Announcement</h3>

              {/* Type */}
              <div className="mb-3">
                <label className="text-sm font-medium text-gray-600 mb-2 block">Type</label>
                <div className="flex flex-wrap gap-2">
                  {TYPE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        form.type === opt.value
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <opt.icon className="h-4 w-4" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="mb-3">
                <label className="text-sm font-medium text-gray-600 mb-1 block">Title</label>
                <Input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={form.type === 'admin_message' ? "A Message from Dr. Ganz" : "PractCom Update — May 2026"}
                />
              </div>

              {/* Sender Name */}
              {form.type === 'admin_message' && (
                <div className="mb-3">
                  <label className="text-sm font-medium text-gray-600 mb-1 block">From</label>
                  <Input
                    value={form.senderName}
                    onChange={e => setForm(f => ({ ...f, senderName: e.target.value }))}
                    placeholder="Dr. Ganz"
                  />
                </div>
              )}

              {/* Version */}
              {form.type === 'update' && (
                <div className="mb-3">
                  <label className="text-sm font-medium text-gray-600 mb-1 block">Version (optional)</label>
                  <Input
                    value={form.version}
                    onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                    placeholder="2.6"
                  />
                </div>
              )}

              {/* Items */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-600 mb-2 block">Message Items</label>
                {form.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <Input
                      value={item.text}
                      onChange={e => {
                        const items = [...form.items];
                        items[idx] = { ...items[idx], text: e.target.value };
                        setForm(f => ({ ...f, items }));
                      }}
                      placeholder={`Point ${idx + 1}`}
                      className="flex-1"
                    />
                    {form.items.length > 1 && (
                      <button onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-600 p-1">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={() => setForm(f => ({ ...f, items: [...f.items, { text: '', icon: null }] }))} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  + Add another item
                </button>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Send className="h-4 w-4 mr-1" />
                  Publish
                </Button>
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Existing Announcements */}
          {loading ? (
            <p className="text-gray-500 text-center py-8">Loading...</p>
          ) : announcements.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No announcements yet. Click "New Announcement" to create one.</p>
          ) : (
            <div className="space-y-3">
              {announcements.map(ann => {
                const typeOpt = TYPE_OPTIONS.find(t => t.value === ann.type) || TYPE_OPTIONS[0];
                return (
                  <div key={ann.id} className={`p-4 rounded-lg border ${ann.isActive ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <typeOpt.icon className="h-4 w-4 text-gray-500" />
                          <span className="font-semibold text-sm">{ann.title}</span>
                          {ann.version && <Badge variant="outline" className="text-xs">v{ann.version}</Badge>}
                          {ann.isActive ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-600 text-xs">Inactive</Badge>
                          )}
                        </div>
                        {ann.senderName && <p className="text-xs text-gray-500">From: {ann.senderName}</p>}
                        <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                          {ann.items?.map((item, i) => <li key={i}>- {item.text}</li>)}
                        </ul>
                        <p className="text-xs text-gray-400 mt-2">{new Date(ann.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <button onClick={() => toggleActive(ann.id, ann.isActive)} className="p-2 rounded hover:bg-gray-100" title={ann.isActive ? 'Deactivate' : 'Activate'}>
                          {ann.isActive ? <ToggleRight className="h-5 w-5 text-green-600" /> : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                        </button>
                        <button onClick={() => deleteAnnouncement(ann.id)} className="p-2 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
